import {
  useEffect,
  useEffectEvent,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import type { DocumentEditOperationDTO, DocumentModelDTO } from '@shared/schemas/document-model';
import { toast } from 'sonner';
import { isInlineEditableBlock, isRewritableBlock } from './block-text';
import { findBlock, getSectionNouns, listEditableUnits } from './canvas-config';
import { CanvasSectionToolbar } from './CanvasSectionToolbar';
import {
  buildInsertSectionOperation,
  buildRewriteBlockOperation,
  buildRewriteSectionOperation,
  buildUpdateBlockOperation,
} from './edit-operations';
import { postToFrame, readBridgeMessage, type BridgeRect } from './preview-bridge';

const TOOLBAR_HEIGHT = 40;
const TOOLBAR_GAP = 6;
const HOVER_GRACE_MS = 280;
// Quand la souris traverse d'autres blocs pour rejoindre la barre, celle-ci ne doit pas sauter de cible.
const HOVER_SWITCH_DELAY_MS = 220;

type Target = { blockId: string | null; rect: BridgeRect; sectionId: string };

export type ScrollRequest = { nonce: number; sectionId: string };

type CanvasPreviewFrameProps = {
  /** La version affichée est terminée et rattachée à un document : l'édition est possible. */
  canEdit: boolean;
  html: string;
  isSaving: boolean;
  model: DocumentModelDTO | null;
  onOperations: (operations: DocumentEditOperationDTO[], scrollToSectionId?: string) => void;
  scrollRequest: ScrollRequest | null;
  title: string;
};

const EDIT_FAILURE_MESSAGES = {
  empty: 'Le texte ne peut pas être vide.',
  invalid: 'Ce texte dépasse la longueur autorisée pour ce bloc.',
  unsupported: 'Ce bloc ne peut pas être modifié directement.',
} as const;

export function CanvasPreviewFrame({
  canEdit,
  html,
  isSaving,
  model,
  onOperations,
  scrollRequest,
  title,
}: CanvasPreviewFrameProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const hideTimerRef = useRef<number | undefined>(undefined);
  const switchTimerRef = useRef<number | undefined>(undefined);
  const isInteractingRef = useRef(false);
  const savedScrollTopRef = useRef(0);
  const handledScrollNonceRef = useRef<number | null>(null);
  // La hauteur est celle du dernier document : elle est conservée pendant un rechargement
  // pour que la position de défilement du conteneur ne soit pas écrasée.
  const [frameHeight, setFrameHeight] = useState<number | null>(null);
  const [readyHtml, setReadyHtml] = useState<string | null>(null);
  const [hovered, setHovered] = useState<Target | null>(null);
  const [selected, setSelected] = useState<Target | null>(null);
  const [editingBlockId, setEditingBlockId] = useState<string | null>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [previousHtml, setPreviousHtml] = useState(html);
  const isFrameReady = readyHtml === html;

  if (previousHtml !== html) {
    // Nouveau document : les cibles pointent vers un DOM qui n'existe plus.
    setPreviousHtml(html);
    setHovered(null);
    setSelected(null);
    setEditingBlockId(null);
  }

  useLayoutEffect(() => {
    savedScrollTopRef.current = scrollRef.current?.scrollTop ?? 0;
  }, [html]);

  function clearTargets() {
    window.clearTimeout(hideTimerRef.current);
    window.clearTimeout(switchTimerRef.current);
    setHovered(null);
    setSelected(null);
  }

  function scheduleHide() {
    window.clearTimeout(hideTimerRef.current);
    hideTimerRef.current = window.setTimeout(() => {
      if (!isInteractingRef.current) {
        setHovered(null);
      }
    }, HOVER_GRACE_MS);
  }

  const handleMessage = useEffectEvent((event: MessageEvent) => {
    const message = readBridgeMessage(event, iframeRef.current?.contentWindow);
    if (!message) {
      return;
    }
    switch (message.type) {
      case 'inersio:ready':
      case 'inersio:resize':
        setFrameHeight(Math.ceil(message.height));
        if (message.type === 'inersio:ready') {
          setReadyHtml(html);
          if (scrollRef.current) {
            scrollRef.current.scrollTop = savedScrollTopRef.current;
          }
        }
        break;
      case 'inersio:hover':
        if (!selected && !editingBlockId) {
          window.clearTimeout(hideTimerRef.current);
          window.clearTimeout(switchTimerRef.current);
          const next = { blockId: message.blockId ?? null, rect: message.rect, sectionId: message.sectionId };
          const isSameTarget = hovered?.sectionId === next.sectionId && hovered.blockId === next.blockId;
          if (!hovered || isSameTarget) {
            setHovered(next);
          } else {
            switchTimerRef.current = window.setTimeout(() => setHovered(next), HOVER_SWITCH_DELAY_MS);
          }
        }
        break;
      case 'inersio:select':
        if (!editingBlockId) {
          window.clearTimeout(hideTimerRef.current);
          setSelected({ blockId: message.blockId ?? null, rect: message.rect, sectionId: message.sectionId });
        }
        break;
      case 'inersio:block-edited': {
        setEditingBlockId(null);
        const result = buildUpdateBlockOperation({
          block: findBlock(model, message.sectionId, message.blockId),
          html: message.html,
          sectionId: message.sectionId,
        });
        if (result.ok) {
          clearTargets();
          onOperations([result.operation], message.sectionId);
        } else if (result.reason !== 'unchanged') {
          toast.error(EDIT_FAILURE_MESSAGES[result.reason]);
        }
        break;
      }
      case 'inersio:edit-cancelled':
        setEditingBlockId(null);
        break;
    }
  });

  useEffect(() => {
    const listener = (event: MessageEvent) => handleMessage(event);
    window.addEventListener('message', listener);
    return () => window.removeEventListener('message', listener);
  }, []);

  useEffect(() => () => {
    window.clearTimeout(hideTimerRef.current);
    window.clearTimeout(switchTimerRef.current);
  }, []);

  // Retour à la section éditée une fois le nouvel aperçu chargé.
  useEffect(() => {
    if (!scrollRequest || !isFrameReady || handledScrollNonceRef.current === scrollRequest.nonce) {
      return;
    }
    handledScrollNonceRef.current = scrollRequest.nonce;
    postToFrame(iframeRef.current?.contentWindow, { sectionId: scrollRequest.sectionId, type: 'inersio:scroll-to' });
  }, [isFrameReady, scrollRequest]);

  const target = canEdit && !editingBlockId ? (selected ?? hovered) : null;
  const hasTarget = target !== null;

  useEffect(() => {
    if (!hasTarget) {
      return;
    }
    const container = scrollRef.current;
    const onScroll = () => setScrollTop(container?.scrollTop ?? 0);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isInteractingRef.current) {
        clearTargets();
      }
    };
    onScroll();
    container?.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('keydown', onKeyDown);
    return () => {
      container?.removeEventListener('scroll', onScroll);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [hasTarget]);

  function submit(operations: DocumentEditOperationDTO[], scrollToSectionId?: string) {
    clearTargets();
    onOperations(operations, scrollToSectionId);
  }

  const units = model ? listEditableUnits(model) : [];
  const unit = target ? units.find((candidate) => candidate.id === target.sectionId) : undefined;
  const block = target?.blockId && model ? findBlock(model, target.sectionId, target.blockId) : null;
  const nouns = getSectionNouns(model?.kind ?? 'report');

  const toolbarTop = target
    ? Math.min(
      Math.max(target.rect.top - TOOLBAR_HEIGHT - TOOLBAR_GAP, scrollTop + TOOLBAR_GAP),
      Math.max(target.rect.top + target.rect.height - TOOLBAR_HEIGHT - TOOLBAR_GAP, target.rect.top + TOOLBAR_GAP),
    )
    : 0;

  return (
    <div
      className="relative h-full overflow-y-auto overscroll-contain bg-muted/35"
      data-testid="document-canvas-scroll"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          clearTargets();
        }
      }}
      ref={scrollRef}
    >
      <div className="relative mx-auto min-h-full w-full max-w-[64rem]">
        <iframe
          className="block w-full border-0 bg-background"
          data-testid="document-canvas-frame"
          onMouseLeave={scheduleHide}
          ref={iframeRef}
          referrerPolicy="no-referrer"
          sandbox="allow-scripts"
          srcDoc={html}
          style={frameHeight === null ? { minHeight: '70vh' } : { height: frameHeight }}
          title={`Aperçu de ${title}`}
        />
        {target && unit ? (
          <CanvasSectionToolbar
            blockId={target.blockId}
            canDelete={units.length > 1}
            canEditBlock={isInlineEditableBlock(block)}
            canRewriteBlock={isRewritableBlock(block)}
            disabled={isSaving}
            key={`${target.sectionId}:${target.blockId ?? ''}`}
            nouns={nouns}
            onBeginEdit={() => {
              if (!target.blockId) {
                return;
              }
              postToFrame(iframeRef.current?.contentWindow, { blockId: target.blockId, type: 'inersio:begin-edit' });
              setEditingBlockId(target.blockId);
              clearTargets();
            }}
            onDelete={() => submit([{ op: 'delete_section', sectionId: target.sectionId }])}
            onInsertAfter={({ instruction, title: sectionTitle }) => {
              const operation = buildInsertSectionOperation({
                afterSectionId: target.sectionId,
                instruction,
                title: sectionTitle,
              });
              if (operation) {
                submit([operation]);
              }
            }}
            onInteractionChange={(active) => {
              isInteractingRef.current = active;
              if (!active) {
                scheduleHide();
              }
            }}
            onPointerEnter={() => {
              window.clearTimeout(hideTimerRef.current);
              window.clearTimeout(switchTimerRef.current);
            }}
            onPointerLeave={scheduleHide}
            onRewriteBlock={(instruction) => {
              const operation = target.blockId
                ? buildRewriteBlockOperation({ blockId: target.blockId, instruction, sectionId: target.sectionId })
                : null;
              if (operation) {
                submit([operation], target.sectionId);
              }
            }}
            onRewriteSection={(instruction) => (
              submit([buildRewriteSectionOperation(target.sectionId, instruction)], target.sectionId)
            )}
            sectionId={target.sectionId}
            sectionTitle={unit.title}
            style={{
              // Ancrée par la droite sur des pixels entiers : aucun transform, donc un texte net.
              right: `calc(100% - max(${Math.round(target.rect.left + target.rect.width)}px, 20rem))`,
              top: Math.round(toolbarTop),
            }}
          />
        ) : null}
      </div>
      {editingBlockId ? (
        <p
          className="pointer-events-none sticky bottom-3 mx-auto w-fit rounded-full border border-border/70 bg-popover/95 px-3 py-1.5 text-xs text-muted-foreground shadow-md"
          role="status"
        >
          Modification du texte — Entrée pour valider, Échap pour annuler
        </p>
      ) : null}
    </div>
  );
}
